import { parse, stringify } from "qs";
import { createRouter, createWebHistory, type LocationQuery, type LocationQueryValue } from "vue-router";

import { config } from "@/config";
import {
  fetchProductsDataGuard,
  fetchSearchDataGuard,
  fetchUserDataGuard,
  ordbogenAccountGuard,
  orderIdParameterGuard,
  packageParameterGuard,
  redirectLegacySearchGuard,
  redirectSearchEntryGuard,
  resetFocusGuard,
  resetSearchStoreGuard,
  searchViewTypeGuard,
  updateSearchStoreGuard
} from "@/router/guards";
import { notifyNgUrlChangeHook } from "@/router/hooks";

const base = config.app.isMobile ? `${config.app.language}/mobile` : config.app.language;

export const router = createRouter({
  history: createWebHistory(base),
  routes: [
    {
      name: "home",
      path: "/",
      component: () => import("@/views/LeHomeView.vue"),
      beforeEnter: resetSearchStoreGuard()
    },

    // LOGIN
    {
      name: "login",
      path: "/login",
      component: () => import("@/views/LeLoginView.vue")
    },
    {
      name: "uni-login.select-institution",
      path: "/uni-login/select-institution",
      component: () => import("@/views/LeUniloginSelectInstitutionView.vue")
    },

    // SEARCH/ARTICLE
    {
      name: "search",
      path: "/search",
      component: {},
      beforeEnter: redirectSearchEntryGuard(),
      meta: {
        analyticsIgnore: true,
        isSearchRoute: true
      }
    },
    {
      name: "search.classic",
      path: "/search/classic",
      component: () => import("@/views/LeClassicSearchView.vue"),
      meta: {
        analyticsIgnore: true,
        searchViewType: "classic",
        isSearchRoute: true
      }
    },
    {
      name: "search.compact",
      path: "/search/compact",
      component: () => import("@/views/LeCompactSearchView.vue"),
      meta: {
        analyticsIgnore: true,
        searchViewType: "compact",
        isSearchRoute: true
      }
    },

    // SUBSCRIPTION
    {
      name: "subscription",
      path: "/subscription",
      component: () => import("@/views/subscription/LeSubscriptionView.vue")
    },
    {
      name: "subscription.business",
      path: "/subscription/business",
      component: () => import("@/views/subscription/LeSubscriptionBusinessView.vue")
    },
    {
      name: "subscription.private",
      path: "/subscription/private",
      component: () => import("@/views/subscription/LeSubscriptionPrivateView.vue"),
      beforeEnter: [fetchProductsDataGuard()]
    },
    {
      name: "subscription.private.sign-up",
      path: "/subscription/private/sign-up",
      component: () => import("@/views/subscription/purchase/LeSignUpView.vue"),
      beforeEnter: [packageParameterGuard(), fetchProductsDataGuard()],
      meta: {
        backUrl: "/subscription/private"
      }
    },
    {
      name: "subscription.private.confirm-payment",
      path: "/subscription/private/confirm-payment",
      component: () => import("@/views/subscription/purchase/LeConfirmPaymentView.vue"),
      beforeEnter: [packageParameterGuard(), ordbogenAccountGuard(), fetchProductsDataGuard()],
      meta: {
        backUrl: "/subscription/private"
      }
    },
    {
      name: "subscription.private.order-status",
      path: "/subscription/private/order-status",
      component: () => import("@/views/subscription/purchase/LeOrderStatusView.vue"),
      beforeEnter: [orderIdParameterGuard()],
      meta: {
        backUrl: "/subscription/private"
      }
    },

    // ABOUT DICTIONARIES
    {
      name: "about-dictionaries",
      path: "/dictionaries",
      component: () => import("@/views/LeAboutDictionariesView.vue"),
      beforeEnter: [fetchProductsDataGuard()]
    },

    // DOWNLOAD
    {
      name: "download",
      path: "/download",
      component: () => import("@/views/download/LeDownloadView.vue")
    },

    // SUPPORT
    {
      name: "support",
      path: "/support",
      component: () => import("@/views/LeSupportView.vue")
    },

    // POLICIES
    {
      name: "policies",
      path: "/policies",
      component: () => import("@/views/LePoliciesView.vue")
    },

    // TERMS
    {
      name: "terms",
      path: "/terms",
      component: () => import("@/views/LeTermsView.vue")
    },

    // COMPANY DICTIONARY (INTERNE TERMER)
    {
      name: "company-dictionary",
      path: "/firmaordbog",
      component: () => import("@/views/LeCompanyDictionaryView.vue")
    },

    // SIKKER TEKNOLOGI
    {
      name: "sikkerteknologi",
      path: "/sikkerteknologi",
      component: () => import("@/views/LeSikkerTeknologiView.vue")
    },

    // GYLDENDAL MIGRATION
    {
      name: "gyldendal-migration",
      path: "/gyldendal-migration",
      component: () => import("@/views/LeGyldendalMigrationView.vue"),
      beforeEnter: [fetchProductsDataGuard()]
    },

    // 404
    {
      path: "/:path(.*)",
      component: {}
    }
  ],
  parseQuery: (search): LocationQuery => parse(search) as Record<string, LocationQueryValue | LocationQueryValue[]>,
  stringifyQuery: stringify,
  scrollBehavior(to, from) {
    const routeName = to.name?.toString() ?? "";
    if (["search", "search.classic"].includes(routeName)) {
      return { top: 0 };
    }

    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }

    if (to.path !== from.path) {
      return { top: 0, left: 0 };
    }
  }
});

router.beforeEach(resetFocusGuard());
router.beforeEach(fetchUserDataGuard());
router.beforeEach(fetchSearchDataGuard());
router.beforeEach(redirectLegacySearchGuard());
router.beforeEach(searchViewTypeGuard());
router.beforeEach(updateSearchStoreGuard());

router.afterEach(notifyNgUrlChangeHook());
